import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { t } from "i18next";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
  Input,
  FormGroup,
  Badge,
} from "reactstrap";
import { MapContainer, TileLayer, Marker, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useMenuItemsStore } from "../../../stores/Assets/menuItems";
import { FormControlLabel, Switch } from "@mui/material";

// Custom Leaflet icon
const customIcon = new L.Icon({
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
  shadowSize: [41, 41],
});

const parseTags = (tags) => {
  if (Array.isArray(tags)) return [...tags];
  if (typeof tags === "string") {
    try {
      const parsed = JSON.parse(tags);
      return Array.isArray(parsed) ? [...parsed] : [];
    } catch {
      return [];
    }
  }
  return [];
};

const EditItem = ({
  menuId,
  rowData,
  showEditItemModal,
  toggleEditItemModal,
  image_url,
}) => {
  const [existingImages, setExistingImages] = useState([]); // Track existing images
  const [imagesToDelete, setImagesToDelete] = useState([]); // Track images to delete
  const [newImages, setNewImages] = useState([]); // Track new images to add
  const [currentTag, setCurrentTag] = useState("");

  const { updateItem, isLoading } = useMenuItemsStore((state) => state);

  // Handle file selection for new images - only allow 1 image
  const handleImageChange = (event) => {
    const files = Array.from(event.target.files);
    // Only allow 1 image
    const singleFile = files.slice(0, 1);

    if (singleFile.length > 0) {
      // Clear existing new images and add the new one
      newImages.forEach((image) => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });

      // Mark all existing images for deletion when new image is added
      setImagesToDelete([...imagesToDelete, ...existingImages]);
      setExistingImages([]);

      const imageFile = {
        file: singleFile[0],
        preview: URL.createObjectURL(singleFile[0]),
        isNew: true,
      };

      setNewImages([imageFile]);
    }
  };

  // Mark an existing image for deletion
  const handleDeleteExistingImage = (index) => {
    const imageToDelete = existingImages[index];

    setImagesToDelete([...imagesToDelete, imageToDelete]);
    setExistingImages(existingImages.filter((_, i) => i !== index));
  };

  // Remove a new image that hasn't been uploaded yet
  const handleRemoveNewImage = (index) => {
    // Release the object URL to prevent memory leaks
    URL.revokeObjectURL(newImages[index].preview);

    setNewImages(newImages.filter((_, i) => i !== index));
  };

  console.log(rowData);

  // Restore a deleted image - this will delete the current image
  const handleRestoreDeletedImage = (index) => {
    const imageToRestore = imagesToDelete[index];

    // If there's a current new image, mark it for deletion
    if (newImages.length > 0) {
      newImages.forEach((image) => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
      setNewImages([]);
    }

    // Remove from deleted images and add back to existing
    setImagesToDelete(imagesToDelete.filter((_, i) => i !== index));
    setExistingImages([imageToRestore]);
  };

  useEffect(() => {
    if (rowData) {
      // Initialize existing images from rowData

      setExistingImages(rowData?.images || []);
      setImagesToDelete([]); // Reset images to delete
      setNewImages([]); // Reset new images
      // Set initial form values
      formik.setValues({
        title: rowData?.title || "",
        title_ar: rowData?.title_ar || "",
        title_fr: rowData?.title_fr || "",
        description: rowData?.description || "",
        description_ar: rowData?.description_ar || "",
        description_fr: rowData?.description_fr || "",
        price: rowData?.price ?? 0,
        available: rowData?.available,
        isHidden: rowData?.isHidden || false,
        IsListed: rowData?.IsListed ?? true,
        is_promo: rowData?.is_promo || false,
        ad_url: rowData?.ad_url || "",
        promo_price: rowData?.promo_price ?? "",
        type: rowData?.type || "DEFAULT",
        tags: parseTags(rowData?.tags),
      });
    }
  }, [rowData]);

  // Cleanup object URLs on component unmount
  useEffect(() => {
    return () => {
      newImages.forEach((image) => {
        if (image.preview) {
          URL.revokeObjectURL(image.preview);
        }
      });
    };
  }, []);

  const formik = useFormik({
    initialValues: {
      title: rowData?.title || "",
      title_ar: rowData?.title_ar || "",
      title_fr: rowData?.title_fr || "",
      description: rowData?.description || "",
      description_ar: rowData?.description_ar || "",
      description_fr: rowData?.description_fr || "",
      price: rowData?.price ?? 0,
      promo_price: rowData?.promo_price ?? "",
      is_promo: rowData?.is_promo || false,
      available: rowData?.available,
      isHidden: rowData?.isHidden || false,
      IsListed: rowData?.IsListed ?? true,
      type: rowData?.type || "DEFAULT",
      ad_url: rowData?.ad_url || "",
      tags: parseTags(rowData?.tags),
    },
    validationSchema: Yup.object({
      title: Yup.string().optional(),
      title_ar: Yup.string().optional(),
      title_fr: Yup.string().optional(),
      description: Yup.string().optional(),
      description_ar: Yup.string().optional(),
      description_fr: Yup.string().optional(),
      price: Yup.number().optional(),
      promo_price: Yup.number().optional(),
      is_promo: Yup.boolean().optional(),
      available: Yup.boolean().optional(),
      type: Yup.string().oneOf(["DEFAULT", "SERVICE", "AD"]).optional(),
      ad_url: Yup.string().when("type", {
        is: "AD",
        then: (schema) =>
          schema.required(t("Ad URL is required for advertisements")),
        otherwise: (schema) => schema.optional(),
      }),
      isHidden: Yup.boolean().optional(),
      IsListed: Yup.boolean().optional(),
      tags: Yup.array().of(Yup.string().optional()),
    }),
    onSubmit: async (values) => {
      const formData = new FormData();

      // Append text fields
      formData.append("title", values.title);
      formData.append("title_ar", values.title_ar);
      formData.append("title_fr", values.title_fr || "");
      formData.append("description", values.description);
      formData.append("description_ar", values.description_ar);
      formData.append("description_fr", values.description_fr || "");
      // price can be 0 (e.g. SERVICE items); avoid parseInt("") => NaN
      const price = Number(values.price);
      formData.append("price", Number.isFinite(price) ? price : 0);
      if (values.promo_price !== "" && values.promo_price != null) {
        const promoPrice = Number(values.promo_price);
        if (Number.isFinite(promoPrice)) {
          formData.append("promo_price", promoPrice);
        }
      }
      formData.append("is_promo", values.is_promo.toString());
      formData.append("available", values.available.toString());
      formData.append("isHidden", values.isHidden.toString());
      formData.append("IsListed", values.IsListed.toString());
      formData.append("type", values.type);
      if (values.type === "AD" && values.ad_url) {
        formData.append("ad_url", values.ad_url);
      }

      formData.append(
        "tags",
        JSON.stringify(Array.isArray(values.tags) ? values.tags : []),
      );

      // Append images to delete
      formData.append("deletedImages", JSON.stringify(imagesToDelete));
      // Append new images
      newImages.forEach(({ file }) => {
        formData.append("newImages", file);
      });
      //print the formData

      try {
        await updateItem(
          parseInt(menuId),
          parseInt(rowData?.item_id),
          formData,
        );
        // Reset form and state
        formik.resetForm();
        setNewImages([]);
        setExistingImages([]);
        setImagesToDelete([]);
        toggleEditItemModal();
      } catch (error) {
        console.error("Error updating item:", error);
      }
    },
  });

  // Get the current image to display (new image takes priority)
  const getCurrentImage = () => {
    if (newImages.length > 0) {
      return {
        url: newImages[0].preview,
        isNew: true,
        file: newImages[0].file,
        name: newImages[0].file.name,
      };
    } else if (existingImages.length > 0) {
      return {
        url: `${image_url}${existingImages[0]}`,
        isExisting: true,
        name: existingImages[0],
      };
    }
    return null;
  };

  const currentImage = getCurrentImage();

  return (
    <Modal isOpen={showEditItemModal} toggle={toggleEditItemModal} size="lg">
      <ModalHeader toggle={toggleEditItemModal}>
        {t("Modify Item: ")} {rowData?.title}
      </ModalHeader>
      <ModalBody>
        <div className="mb-4">
          <h5>{t("Item Image")}</h5>

          {currentImage ? (
            <div
              className="position-relative bg-warning rounded"
              style={{
                boxShadow: "inset 0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                padding: "10px",
              }}
            >
              <div className="position-relative" style={{ height: "300px" }}>
                <img
                  src={currentImage.url}
                  alt="Item Image"
                  style={{
                    width: "100%",
                    height: "300px",
                    objectFit: "contain",
                  }}
                />
                <Button
                  color="danger"
                  size="sm"
                  className="position-absolute"
                  style={{ top: "10px", right: "10px" }}
                  onClick={() => {
                    if (currentImage.isNew) {
                      handleRemoveNewImage(0);
                    } else {
                      handleDeleteExistingImage(0);
                    }
                  }}
                >
                  <i className="ri-delete-bin-line"></i>
                </Button>
                <div
                  className="position-absolute"
                  style={{
                    bottom: "10px",
                    left: "10px",
                    backgroundColor: "rgba(0,0,0,0.5)",
                    color: "white",
                    padding: "5px",
                  }}
                >
                  {currentImage.isExisting ? "Existing Image" : "New Image"}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center p-4 bg-light">
              <p>{t("No image available")}</p>
            </div>
          )}

          <div className="mt-3">
            <Label for="images">{t("Upload New Image")}</Label>
            <Input
              type="file"
              id="images"
              name="images"
              accept="image/*"
              onChange={handleImageChange}
            />
          </div>

          {imagesToDelete.length > 0 && (
            <div className="mt-3">
              <h6>{t("Images marked for deletion:")}</h6>
              <div className="d-flex flex-wrap">
                {imagesToDelete.map((image, index) => (
                  <div key={index} className="position-relative m-1">
                    <img
                      src={`${image_url}${image}`}
                      alt={`Delete ${index}`}
                      style={{
                        width: "60px",
                        height: "60px",
                        objectFit: "cover",
                      }}
                      className="border"
                    />
                    <Button
                      color="success"
                      size="sm"
                      className="position-absolute"
                      style={{
                        top: "0",
                        right: "0",
                        padding: "2px",
                        fontSize: "10px",
                      }}
                      onClick={() => handleRestoreDeletedImage(index)}
                    >
                      ↩
                    </Button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <form
          onSubmit={formik.handleSubmit}
          className="d-flex flex-column gap-1"
        >
          <Label for="title">{t("Title")}</Label>
          <Input
            type="text"
            id="title"
            name="title"
            {...formik.getFieldProps("title")}
            invalid={formik.touched.title && Boolean(formik.errors.title)}
            placeholder={t("Enter item title")}
          />
          <Label for="title_ar">{t("Title (AR)")}</Label>
          <Input
            type="text"
            id="title_ar"
            name="title_ar"
            {...formik.getFieldProps("title_ar")}
            invalid={formik.touched.title_ar && Boolean(formik.errors.title_ar)}
            placeholder={t("Enter item title_ar")}
          />
          <Label for="title_fr">{t("Title (FR)")}</Label>
          <Input
            type="text"
            id="title_fr"
            name="title_fr"
            {...formik.getFieldProps("title_fr")}
            invalid={formik.touched.title_fr && Boolean(formik.errors.title_fr)}
            placeholder={t("Enter item title_fr")}
          />
          <Label for="description">{t("Description")}</Label>
          <Input
            type="textarea"
            id="description"
            name="description"
            {...formik.getFieldProps("description")}
            placeholder={t("Enter item description")}
          />
          <Label for="description_ar">{t("Description (AR)")}</Label>
          <Input
            type="textarea"
            id="description_ar"
            name="description_ar"
            {...formik.getFieldProps("description_ar")}
            placeholder={t("Enter item description in Arabic")}
          />
          <Label for="description_fr">{t("Description (FR)")}</Label>
          <Input
            type="textarea"
            id="description_fr"
            name="description_fr"
            {...formik.getFieldProps("description_fr")}
            placeholder={t("Enter item description in French")}
          />
          <Label for="price">{t("Price")}</Label>
          <Input
            type="number"
            id="price"
            name="price"
            {...formik.getFieldProps("price")}
            placeholder={t("Enter item price")}
          />
          <Label for="promo_price">{t("Promo Price")}</Label>
          <Input
            type="number"
            id="promo_price"
            name="promo_price"
            {...formik.getFieldProps("promo_price")}
            placeholder={t("Enter promo price")}
          />
          <FormControlLabel
            control={
              <Switch
                checked={formik.values.is_promo}
                onChange={(event) => {
                  formik.setFieldValue("is_promo", event.target.checked);
                }}
              />
            }
            label="Show in promo section"
          />
          <small className="text-muted d-block mb-2">
            Note: Enable this to push the item into the promo section
          </small>

          <FormControlLabel
            control={
              <Switch
                checked={formik.values.available}
                onChange={(event) => {
                  formik.setFieldValue("available", event.target.checked);
                }}
              />
            }
            label="Available"
          />
          <small className="text-muted d-block mb-2">
            Note: Available=false means the item will show but it will say "out
            of stock"
          </small>

          <FormControlLabel
            control={
              <Switch
                checked={formik.values.isHidden}
                onChange={(event) => {
                  formik.setFieldValue("isHidden", event.target.checked);
                }}
              />
            }
            label="Hidden"
          />
          <small className="text-muted d-block mb-2">
            Note: Hidden means the item will not appear in the menu. Available and
            Hidden can be set independently.
          </small>

          <FormControlLabel
            control={
              <Switch
                checked={formik.values.IsListed}
                onChange={(event) => {
                  formik.setFieldValue("IsListed", event.target.checked);
                }}
              />
            }
            label="Listed"
          />
          <small className="text-muted d-block mb-2">
            Note: Listed controls whether the item shows in station stock menus.
          </small>

          {/* Type Selector */}
          <Label for="type">{t("Item Type")}</Label>
          <Input
            type="select"
            id="type"
            name="type"
            {...formik.getFieldProps("type")}
          >
            <option value="DEFAULT">Default</option>
            <option value="SERVICE">Service</option>
            <option value="AD">Advertisement</option>
          </Input>

          {/* Ad URL Field - Only show when type is AD */}
          {formik.values.type === "AD" && (
            <>
              <Label for="ad_url">{t("Advertisement URL")}</Label>
              <Input
                type="url"
                id="ad_url"
                name="ad_url"
                {...formik.getFieldProps("ad_url")}
                placeholder="https://example.com"
                invalid={formik.touched.ad_url && Boolean(formik.errors.ad_url)}
              />
              {formik.touched.ad_url && formik.errors.ad_url && (
                <div className="text-danger">{formik.errors.ad_url}</div>
              )}
            </>
          )}

          {/* Tags Section */}
          <FormGroup>
            <Label for="tags">{t("Tags")}</Label>
            <div className="d-flex gap-2">
              <Input
                id="tagInput"
                name="tagInput"
                type="text"
                value={currentTag}
                onChange={(e) => setCurrentTag(e.target.value)}
                placeholder={t("Enter a tag")}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (currentTag.trim()) {
                      formik.setFieldValue("tags", [
                        ...formik.values.tags,
                        currentTag.trim(),
                      ]);
                      setCurrentTag("");
                    }
                  }
                }}
              />
              <Button
                color="secondary"
                type="button"
                onClick={() => {
                  if (currentTag.trim()) {
                    formik.setFieldValue("tags", [
                      ...formik.values.tags,
                      currentTag.trim(),
                    ]);
                    setCurrentTag("");
                  }
                }}
              >
                {t("Add")}
              </Button>
            </div>
            <div className="mt-2 d-flex flex-wrap gap-2">
              {Array.isArray(formik.values.tags) &&
                formik.values.tags.map((tag, index) => (
                  <Badge
                    key={index}
                    color="primary"
                    className="p-2 d-flex align-items-center gap-2"
                  >
                    {tag}
                    <span
                      style={{ cursor: "pointer", fontWeight: "bold" }}
                      onClick={() => {
                        const newTags = [...formik.values.tags];
                        newTags.splice(index, 1);
                        formik.setFieldValue("tags", newTags);
                      }}
                    >
                      ×
                    </span>
                  </Badge>
                ))}
            </div>
          </FormGroup>

          <div className="d-flex flex-row align-items-center justify-content-center gap-2 mt-2">
            <Button type="submit" color="success" disabled={isLoading}>
              {isLoading ? <Spinner size={"sm"} /> : <span>{t("Save")}</span>}
            </Button>
            <Button
              color="danger"
              onClick={() => {
                toggleEditItemModal();
                formik.resetForm();
                setNewImages([]);
                setExistingImages(rowData?.images || []);
                setImagesToDelete([]);
              }}
              disabled={isLoading}
            >
              {t("Cancel")}
            </Button>
          </div>
        </form>
      </ModalBody>
    </Modal>
  );
};

export default EditItem;
